import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CssBaseline,
  Sheet,
  Skeleton,
  ThemeProvider,
  Typography,
} from '@mui/joy';
import { Container } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { theme } from '../../main/theme/constants';
import { getPublicPieceTrack } from '../../main/API/shipmentPiece';
import {
  shipmentStatusChipSx,
  shipmentStatusJoyColor as statusColor,
  shipmentStatusLabel as statusLabel,
} from '../../main/utils/shipmentStatusDisplay';

function formatTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  } catch (_) {
    return '—';
  }
}

export default function PublicPieceTrackingScreen() {
  const { t } = useTranslation('ui');
  const { barcode } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPublicPieceTrack(barcode);
      setData(res && res.data ? res.data : null);
    } catch (e) {
      const code404 = e && e.response && e.response.status === 404;
      setError(code404 ? t('publicPieceTracking.notFound') : t('publicPieceTracking.loadFailed'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [barcode, t]);

  useEffect(() => {
    if (barcode) load();
  }, [barcode, load]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'linear-gradient(135deg, #f8fafc 0%, #fff 100%)',
          py: { xs: 3, sm: 6 },
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography level="h2" sx={{ fontWeight: 800, color: '#1f2937' }}>
              {t('publicPieceTracking.pageTitle')}
            </Typography>
            <Typography level="body-sm" sx={{ color: '#6b7280', mt: 0.5 }}>
              {t('publicPieceTracking.subtitle')}
            </Typography>
          </Box>

          <Card variant="outlined" sx={{ borderRadius: '14px', p: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {loading && (
                <Box>
                  <Skeleton level="title-lg" sx={{ mb: 1 }} />
                  <Skeleton level="body-md" />
                  <Skeleton level="body-md" sx={{ mt: 1 }} />
                  <Skeleton level="body-md" sx={{ mt: 1 }} />
                </Box>
              )}

              {!loading && error && (
                <Alert color="danger" variant="soft">
                  {error}
                </Alert>
              )}

              {!loading && !error && data && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <LocalShippingIcon sx={{ fontSize: 32, color: '#5f09c2' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography level="title-lg" sx={{ fontWeight: 700 }}>
                        {data.human_label || '—'}
                      </Typography>
                      <Typography
                        level="body-sm"
                        sx={{ fontFamily: 'monospace', color: 'text.tertiary' }}
                      >
                        {data.barcode}
                      </Typography>
                    </Box>
                    <Chip
                      color={statusColor(data.status)}
                      variant="soft"
                      size="lg"
                      sx={shipmentStatusChipSx(data.status)}
                    >
                      {statusLabel(data.status, t)}
                    </Chip>
                  </Box>

                  {data.shipment && (
                    <Sheet variant="soft" color="neutral" sx={{ p: 1.5, borderRadius: '10px', mb: 2 }}>
                      <Typography level="body-sm">
                        <strong>{t('publicPieceTracking.trackingNumber')}:</strong>{' '}
                        {data.shipment.tracking_number || `#${data.shipment.id}`}
                      </Typography>
                      <Typography level="body-sm">
                        <strong>{t('shipmentPieces.status')}:</strong>{' '}
                        {statusLabel(data.shipment.status, t)}
                      </Typography>
                    </Sheet>
                  )}

                  <Sheet
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      borderColor: 'primary.300',
                      bgcolor: 'primary.softBg',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CheckCircleOutlineIcon color="success" />
                      <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                        {t('publicPieceTracking.lastEvent')}
                      </Typography>
                    </Box>
                    {data.last_event ? (
                      <Box>
                        <Typography level="body-md" sx={{ fontWeight: 600 }}>
                          {data.last_event.event_type_name || data.last_event.event_type || '—'}
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                          {formatTime(data.last_event.timestamp)}
                        </Typography>
                        {(data.last_event.location || data.location) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                              {data.last_event.location || data.location}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        {t('publicPieceTracking.noEvents')}
                      </Typography>
                    )}
                  </Sheet>
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
